import { c as _c } from "react/compiler-runtime";
import { Checkbox, FormControl, FormControlLabel, Stack, Typography } from '@mui/material';
import { ConditionOperatorEnum } from '@repo/api-gw-sdk';
import type { Condition } from '@repo/api-gw-sdk';
import { type CSSProperties, type UIEventHandler } from 'react';
import { defaultOperatorFor } from '@/components/conditions/OperatorSelect';
import { HelpIcon } from '@/components/shared/helpIcon';
import { conditionValues, findValueCondition, isValueCondition, type FilterOption, type FilterProperty, type FilterProps } from '@/types/advanceFilter';
import { TransactionalTextField } from '../shared/transactionalTextField';
import { Virtuoso } from '../shared/virtuoso';
import { TextDetailsTuple } from '../text/textDetailsTuple';
export type { FilterProps };
export const FreeTextFilter = t0 => {
  const $ = _c(34);
  const {
    placeholder,
    property,
    conditions,
    onChange,
    "data-testid": dataTestId,
    showSearchIcon: t1,
    operator: t2
  } = t0;
  const showSearchIcon = t1 === undefined ? true : t1;
  const operator = t2 === undefined ? ConditionOperatorEnum.Contains : t2;
  let T0;
  let T1;
  let t10;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== conditions || $[1] !== dataTestId || $[2] !== placeholder || $[3] !== property.name || $[4] !== showSearchIcon) {
    const condition = findValueCondition(conditions, property.name);
    T1 = FormControl;
    t9 = "outlined";
    t10 = true;
    T0 = TransactionalTextField;
    t3 = showSearchIcon;
    t4 = true;
    t5 = placeholder;
    t6 = true;
    t7 = dataTestId;
    t8 = conditionValues(condition)[0] || "";
    $[0] = conditions;
    $[1] = dataTestId;
    $[2] = placeholder;
    $[3] = property.name;
    $[4] = showSearchIcon;
    $[5] = T0;
    $[6] = T1;
    $[7] = t10;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    T0 = $[5];
    T1 = $[6];
    t10 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
    t9 = $[14];
  }
  let t11;
  if ($[15] !== conditions || $[16] !== onChange || $[17] !== operator || $[18] !== property.name) {
    t11 = value => {
      const cleanValue = (value || "").trim();
      onChange(conditions.filter(x => isValueCondition(x) && x.property !== property.name).concat(cleanValue ? [{
        property: property.name,
        operator,
        value: [cleanValue]
      }] : []));
    };
    $[15] = conditions;
    $[16] = onChange;
    $[17] = operator;
    $[18] = property.name;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== T0 || $[21] !== t11 || $[22] !== t3 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t12 = <T0 showSearchIcon={t3} showClearIcon={t4} placeholder={t5} allowEmptyValue={t6} data-testid={t7} initValue={t8} onChange={t11} />;
    $[20] = T0;
    $[21] = t11;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== T1 || $[30] !== t10 || $[31] !== t12 || $[32] !== t9) {
    t13 = <T1 variant={t9} fullWidth={t10}>{t12}</T1>;
    $[29] = T1;
    $[30] = t10;
    $[31] = t12;
    $[32] = t9;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  return t13;
};
const SELECT_ALL_OPTION_ID = 'select-all';

/** Lists shorter than this render in full; longer ones are virtualized. */
const VIRTUALIZATION_THRESHOLD = 100;
type MultiSelectFilterRowProps = FilterProps & {
  option: FilterOption;
  options: FilterOption[];
  selectedValues: string[];
  dataTestId?: string;
  showNameFirst?: boolean;
  shouldShowId?: boolean;
};
const MultiSelectFilterRow = t0 => {
  const $ = _c(22);
  const {
    option,
    options,
    selectedValues,
    property,
    conditions,
    dataTestId,
    onChange,
    showNameFirst,
    shouldShowId
  } = t0;
  if (option.id === SELECT_ALL_OPTION_ID) {
    let t1;
    if ($[0] !== conditions || $[1] !== onChange || $[2] !== options || $[3] !== property || $[4] !== selectedValues) {
      t1 = <SelectAllFilterItem options={options} selectedResources={selectedValues} onChange={onChange} conditions={conditions} property={property} />;
      $[0] = conditions;
      $[1] = onChange;
      $[2] = options;
      $[3] = property;
      $[4] = selectedValues;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  let t1;
  if ($[6] !== conditions || $[7] !== onChange || $[8] !== option.id || $[9] !== property || $[10] !== selectedValues) {
    t1 = isSelected => {
      const values = isSelected ? [...selectedValues, option.id] : selectedValues.filter(x => x !== option.id);
      const existingCondition = findValueCondition(conditions, property.name);
      onChange(conditions.filter(x_0 => x_0 !== existingCondition).concat(values.length ? [{
        property: property.name,
        operator: existingCondition?.operator ?? defaultOperatorFor(property),
        value: values
      }] : []));
    };
    $[6] = conditions;
    $[7] = onChange;
    $[8] = option.id;
    $[9] = property;
    $[10] = selectedValues;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const toggleValue = t1;
  let t2;
  if ($[12] !== option.id || $[13] !== selectedValues) {
    t2 = selectedValues.includes(option.id);
    $[12] = option.id;
    $[13] = selectedValues;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const t3 = !!shouldShowId;
  const t4 = !!showNameFirst;
  let t5;
  if ($[15] !== dataTestId || $[16] !== option || $[17] !== t2 || $[18] !== t3 || $[19] !== t4 || $[20] !== toggleValue) {
    t5 = <MultiSelectFilterItem option={option} dataTestId={dataTestId} onChange={toggleValue} isChecked={t2} shouldShowId={t3} showNameFirst={t4} />;
    $[15] = dataTestId;
    $[16] = option;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = toggleValue;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
};
export const MultiSelectFilter = t0 => {
  const $ = _c(33);
  let dataTestId;
  let onVirtuosoScroll;
  let options;
  let props;
  let showSelectAll;
  let t1;
  let virtuosoStyles;
  if ($[0] !== t0) {
    ({
      options,
      showSelectAll,
      virtuosoStyles,
      onVirtuosoScroll,
      "data-testid": dataTestId,
      ...props
    } = t0);
    t1 = conditionValues(findValueCondition(props.conditions, props.property.name));
    $[0] = t0;
    $[1] = dataTestId;
    $[2] = onVirtuosoScroll;
    $[3] = options;
    $[4] = props;
    $[5] = showSelectAll;
    $[6] = t1;
    $[7] = virtuosoStyles;
  } else {
    dataTestId = $[1];
    onVirtuosoScroll = $[2];
    options = $[3];
    props = $[4];
    showSelectAll = $[5];
    t1 = $[6];
    virtuosoStyles = $[7];
  }
  const selectedValues = t1;
  let t2;
  if ($[8] !== dataTestId || $[9] !== options || $[10] !== props || $[11] !== selectedValues) {
    t2 = {
      ...props,
      dataTestId,
      options,
      selectedValues
    };
    $[8] = dataTestId;
    $[9] = options;
    $[10] = props;
    $[11] = selectedValues;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const rowProps = t2;
  let t3;
  if ($[13] !== options || $[14] !== showSelectAll) {
    t3 = showSelectAll ? [{
      id: SELECT_ALL_OPTION_ID
    }, ...options] : options;
    $[13] = options;
    $[14] = showSelectAll;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const allOptions = t3;
  if (allOptions.length < VIRTUALIZATION_THRESHOLD) {
    let t4;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        gap: "4px"
      };
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    let t5;
    if ($[17] !== allOptions || $[18] !== rowProps) {
      let t6;
      if ($[20] !== rowProps) {
        t6 = option => <MultiSelectFilterRow key={option.id} {...rowProps} option={option} />;
        $[20] = rowProps;
        $[21] = t6;
      } else {
        t6 = $[21];
      }
      t5 = allOptions.map(t6);
      $[17] = allOptions;
      $[18] = rowProps;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    let t6;
    if ($[22] !== t5) {
      t6 = <Stack sx={t4}>{t5}</Stack>;
      $[22] = t5;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    return t6;
  }
  let t4;
  if ($[24] !== virtuosoStyles) {
    t4 = virtuosoStyles || {
      height: "100%"
    };
    $[24] = virtuosoStyles;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  let t5;
  if ($[26] !== rowProps) {
    t5 = (index, option_0) => <MultiSelectFilterRow key={option_0.id} {...rowProps} option={option_0} />;
    $[26] = rowProps;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== allOptions || $[29] !== onVirtuosoScroll || $[30] !== t4 || $[31] !== t5) {
    t6 = <Virtuoso style={t4} data={allOptions} onScroll={onVirtuosoScroll} itemContent={t5} />;
    $[28] = allOptions;
    $[29] = onVirtuosoScroll;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  return t6;
};
const SelectAllFilterItem = t0 => {
  const $ = _c(18);
  const {
    options,
    selectedResources,
    onChange,
    conditions,
    property
  } = t0;
  let t1;
  if ($[0] !== options) {
    t1 = options.filter(_temp);
    $[0] = options;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectableOptions = t1;
  const t2 = `Select all (${selectableOptions.length})`;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      id: SELECT_ALL_OPTION_ID,
      name: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== conditions || $[5] !== onChange || $[6] !== property || $[7] !== selectableOptions) {
    t4 = newValue => {
      const values = newValue ? selectableOptions.map(_temp2) : [];
      onChange(conditions.filter(x_1 => isValueCondition(x_1) && x_1.property !== property.name).concat(values.length ? [{
        property: property.name,
        operator: defaultOperatorFor(property),
        value: values
      }] : []));
    };
    $[4] = conditions;
    $[5] = onChange;
    $[6] = property;
    $[7] = selectableOptions;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== options || $[10] !== selectedResources) {
    let t6;
    if ($[12] !== selectedResources) {
      t6 = x_2 => selectedResources.includes(x_2.id);
      $[12] = selectedResources;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t5 = options.every(t6);
    $[9] = options;
    $[10] = selectedResources;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t6 = <MultiSelectFilterItem shouldShowId={false} showNameFirst={false} option={t3} onChange={t4} isChecked={t5} />;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
const MultiSelectFilterItem = t0 => {
  const $ = _c(31);
  const {
    option,
    dataTestId,
    onChange,
    isChecked,
    shouldShowId,
    showNameFirst
  } = t0;
  if (option.isGroupTitle) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        paddingY: "8px"
      };
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== option.name) {
      t2 = <Typography variant="overline" sx={t1}>{option.name}</Typography>;
      $[1] = option.name;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignItems: "center",
      display: "flex",
      width: "100%"
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = `${dataTestId ? `${dataTestId}-` : ""}${option.id}`;
  let t3;
  if ($[4] !== t2) {
    t3 = {
      "data-testid": t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = t3 as Record<string, string>;
  let t5;
  if ($[6] !== t4) {
    t5 = {
      input: t4
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== onChange) {
    t6 = (event, value) => onChange(value);
    $[8] = onChange;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== isChecked || $[11] !== t5 || $[12] !== t6) {
    t7 = <Checkbox slotProps={t5} className="p-[8px] mr-[4px]" size="small" checked={isChecked} onChange={t6} />;
    $[10] = isChecked;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      alignItems: "center",
      gap: "12px"
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== option.hideName || $[16] !== option.id || $[17] !== option.name || $[18] !== shouldShowId || $[19] !== showNameFirst) {
    t9 = !option.hideName && <TextDetailsTuple primaryProp={shouldShowId ? showNameFirst ? option.name : option.id : undefined} secondaryProp={showNameFirst ? option.id : option.name} />;
    $[15] = option.hideName;
    $[16] = option.id;
    $[17] = option.name;
    $[18] = shouldShowId;
    $[19] = showNameFirst;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== option.description) {
    t10 = option.description && <HelpIcon title={option.description} className="absolute right-0 cursor-default" tooltipProps={{
      slotProps: {
        tooltip: {
          sx: {
            padding: "16px"
          }
        }
      }
    }} />;
    $[21] = option.description;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== option.Image || $[24] !== t10 || $[25] !== t9) {
    t11 = <Stack direction="row" className="w-[216px] relative" sx={t8}>{option.Image}{t9}{t10}</Stack>;
    $[23] = option.Image;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== option.id || $[28] !== t11 || $[29] !== t7) {
    t12 = <FormControlLabel key={option.id} className="group/help" sx={t1} control={t7} label={t11} />;
    $[27] = option.id;
    $[28] = t11;
    $[29] = t7;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
};
function _temp(x) {
  return !x.isGroupTitle;
}
function _temp2(x_0) {
  return x_0.id;
}